import AddIcon from "@mui/icons-material/Add";
// TODO: wrap Alert, CircularProgress, MenuItem, Select, TextField as foundation primitives.
// The CircularProgress below should consolidate onto the Spinner foundation
// primitive (#2980) — this file currently renders both spinner idioms.
import {
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import * as React from "react";

import { RobotoButton } from "@/shared/components/RobotoButton";
import {
  TimeDisplayMode,
  useTimeDisplayMode,
  useWorkspaceTimeBounds,
} from "@/shared/components/visualization/WorkspaceCtx";
import { Button, Spinner } from "@/shared/foundation/primitives";
import { useDebouncedCallback, usePopupState } from "@/shared/hooks";
import { TimeBoundsSource } from "@/shared/state/visualization/schema";

import {
  ClearFiltersAction,
  DeleteFilterAction,
  SetCombinatorAction,
  SetFilterAction,
  type ColumnFiltersAction,
} from "../columnFilters/actions";
import { ALL_COLUMNS_ID, LOG_TIME_ID } from "../columnFilters/columnDefinition";
import { ColumnFilterPopover } from "../columnFilters/ColumnFilterPopover";
import {
  conditionMeta,
  getConditionsForDataType,
  getFilterPlaceholder,
  isDatetimeColumn,
  isValuelessCondition,
  needsSecondValue,
  shouldUseDatetimePicker,
} from "../columnFilters/conditionMeta";
import { DatetimeFilterInput } from "../columnFilters/DatetimeFilterInput";
import {
  type FilterStageColorVars,
  stageColorStyle,
} from "../columnFilters/filterStageColors";
import { isValidRegex } from "../columnFilters/regexUtils";
import {
  canCommitLogtime,
  resolveLogTimeValue,
} from "../columnFilters/resolveLogTimeValue";
import {
  type ColumnFilter,
  type ColumnFiltersState,
  type FilterCondition,
  hasAllColumnsFilter,
  hasPerColumnFilters,
  isExclusivityViolation,
} from "../columnFilters/state";
import { useColumnFilterActions } from "../columnFilters/useColumnFilterActions";
import { IndexState } from "../IndexState";
import type { FilterDisplayMode } from "../LogFilter";

import styles from "./FilterBar.module.css";
import { FilterDisplayModeToggle } from "./FilterDisplayModeToggle";
import { type StageLabelOptions, stageLabel } from "./filterLabels";

// TODO: Add component-level tests for FilterBar using Vitest browser mode
// (see ENG-2009 for browser testing infrastructure). Current test coverage
// is limited to pure logic (reducer, search engine, highlight utils).
// Key scenarios to test: filter add/delete flow, __all__ exclusivity UI
// feedback, debounce behavior, datetime picker integration, pill rendering.

// Scoped class that drops MenuItem font-size to 0.8125rem so the Select
// dropdown matches the trigger (overridden in .builderRow :global rule).
const SELECT_MENU_PROPS = {
  PaperProps: { className: styles.selectMenu },
} as const;

interface FilterBarProps {
  hidden: boolean;
  onSearch: (
    columnFilters: Record<string, ColumnFilter>,
    signal?: AbortSignal,
  ) => void;
  columnFilters: ColumnFiltersState;
  columnFiltersDispatch: React.Dispatch<ColumnFiltersAction>;
  /**
   * True while the worker is applying (or clearing) a filter. The pills render
   * the intended filter state immediately; on large topics the table lags by
   * seconds, so the builder row shows a spinner until the application settles.
   */
  isApplyingFilter: boolean;
  /** Map from `${columnId}:${stageIndex}` to a stage-color slot. Owned by LogPanel. */
  colorMap: Map<string, number>;
  /** Color variables for each stage slot. Owned by LogPanel. */
  stageColors: FilterStageColorVars[];
  /** Whether non-matching rows are hidden or shown at reduced opacity. */
  filterDisplayMode: FilterDisplayMode;
  /** Called when the user toggles between hide and dim display modes. */
  onFilterDisplayModeChange: (mode: FilterDisplayMode) => void;
  searchIndexState: IndexState;
  /**
   * Whether the panel has enough logs that building the search index requires
   * explicit user confirmation (memory caveat). When false, the builder row is
   * replaced with a progress indicator while building; when true, the user
   * must click "Build Index" first.
   */
  needsBuildConfirmation: boolean;
  /** Panel's total log count, used in the confirmation alert copy. */
  topicMessageCount: number;
  /** Called when the user accepts the build-index confirmation. */
  onBuildIndex: () => void;
  /** Called when the user dismisses the build-index confirmation. */
  onDismissBuildPrompt: () => void;
}

export function FilterBar({
  hidden,
  onSearch,
  isApplyingFilter,
  searchIndexState,
  columnFilters,
  columnFiltersDispatch,
  colorMap,
  stageColors,
  filterDisplayMode,
  onFilterDisplayModeChange,
  needsBuildConfirmation,
  topicMessageCount,
  onBuildIndex,
  onDismissBuildPrompt,
}: FilterBarProps) {
  const [timeDisplayMode] = useTimeDisplayMode();
  const timeBounds = useWorkspaceTimeBounds({
    timeBoundsSource: TimeBoundsSource.FilesInWorkspace,
  });
  const earliestTime = timeBounds.earliest ?? null;
  const [selectedColumnId, setSelectedColumnId] =
    React.useState<string>(ALL_COLUMNS_ID);
  const [filterText, setFilterText] = React.useState("");
  const [filterText2, setFilterText2] = React.useState("");
  const [condition, setCondition] = React.useState<FilterCondition>("contains");
  const filterInputRef = React.useRef<HTMLInputElement | null>(null);

  // Focus the value input as soon as the builder row is interactable. Runs when
  // the bar is revealed and again when an in-flight index build completes, so
  // the user can type immediately whether or not the index was already built.
  React.useEffect(
    function focusFilterInputWhenAvailable() {
      if (hidden || searchIndexState !== IndexState.Built) {
        return;
      }
      filterInputRef.current?.focus();
    },
    [hidden, searchIndexState],
  );

  const selectedColumn = columnFilters.columns[selectedColumnId];
  const selectedDataType = selectedColumn?.dataType;
  const availableConditions = getConditionsForDataType(selectedDataType);

  const disabled = searchIndexState !== IndexState.Built;

  // Stabilize onSearch identity so debouncedSearch doesn't change on every render.
  const onSearchRef = React.useRef(onSearch);
  onSearchRef.current = onSearch;

  const stableOnSearch = React.useCallback(
    (filters: Record<string, ColumnFilter>, signal?: AbortSignal) =>
      onSearchRef.current(filters, signal),
    [],
  );

  // Trigger search when column filters change or the search index becomes ready.
  // The searchIndexState dependency ensures that saved filters are applied once the
  // index finishes building — without it, filters restored on page load race against
  // index construction and can run against an empty index (producing 0 results with
  // no retry).
  const debouncedSearch = useDebouncedCallback(stableOnSearch, 300);
  const abortRef = React.useRef<AbortController | null>(null);

  React.useEffect(
    function onFiltersChange() {
      if (hidden || searchIndexState !== IndexState.Built) {
        return;
      }
      abortRef.current?.abort();
      const abort = new AbortController();
      debouncedSearch(columnFilters.filters, abort.signal);
      abortRef.current = abort;
      return () => {
        abort.abort();
      };
    },
    // filterDisplayMode is captured by onSearch's closure (via onSearchRef),
    // so toggling it must re-run the search to apply the new mode.
    [
      debouncedSearch,
      columnFilters.filters,
      hidden,
      searchIndexState,
      filterDisplayMode,
    ],
  );

  // Reset condition when column changes and the current condition is unavailable
  const handleColumnChange = React.useCallback(
    (nextColumnId: string) => {
      setSelectedColumnId(nextColumnId);
      const nextDataType = columnFilters.columns[nextColumnId]?.dataType;
      const nextConditions = getConditionsForDataType(nextDataType);
      if (!nextConditions.includes(condition)) {
        setCondition(nextConditions[0]);
      }
    },
    [columnFilters.columns, condition],
  );

  const handleAdd = () => {
    // Defense-in-depth: reject if mutual exclusivity would be violated.
    if (isExclusivityViolation(selectedColumnId, columnFilters)) {
      return;
    }
    const valueless = isValuelessCondition(condition);
    const hasTwoValues = needsSecondValue(condition);
    if (!valueless && filterText.trim() === "") {
      return;
    }
    if (hasTwoValues && filterText2.trim() === "") {
      return;
    }
    if (
      condition === "matches_regex" &&
      filterText !== "" &&
      !isValidRegex(filterText)
    ) {
      return;
    }
    // Validate numeric input for numeric conditions.
    // Logtime columns skip this gate because resolveLogTimeValue handles
    // format validation itself (returning null for invalid input).
    // Without the skip, ElapsedTime's hh:mm:ss format would be rejected by
    // Number.isFinite(). Log and Elapsed modes pass Number() naturally
    // (plain decimal seconds), but skipping is still correct — resolution
    // is the authoritative validator for all logtime formats.
    const isLogTime = selectedColumnId === LOG_TIME_ID;
    if (isLogTime && !canCommitLogtime(timeDisplayMode, earliestTime)) {
      return;
    }
    if (conditionMeta[condition].numericOnly && !isLogTime) {
      // Guard against hex/octal/binary literals that Number() accepts
      if (/^0[xXoObB]/.test(filterText.trim())) {
        return;
      }
      if (!Number.isFinite(Number(filterText.trim()))) {
        return;
      }
      if (hasTwoValues) {
        if (/^0[xXoObB]/.test(filterText2.trim())) {
          return;
        }
        if (!Number.isFinite(Number(filterText2.trim()))) {
          return;
        }
      }
    }
    // Resolve logtime values from display-mode format to the canonical
    // nanosecond epoch string that the search engine stores internally.
    // Returns null if the input cannot be parsed — reject silently.
    const resolveValue = (v: string) =>
      isLogTime ? resolveLogTimeValue(v, timeDisplayMode, earliestTime) : v;

    const value = valueless ? "" : resolveValue(filterText.trim());
    if (value === null) {
      return;
    }
    const value2 = hasTwoValues ? resolveValue(filterText2.trim()) : undefined;
    if (value2 === null) {
      return;
    }

    columnFiltersDispatch(
      new SetFilterAction({
        columnId: selectedColumnId,
        value,
        condition,
        ...(value2 !== undefined && { value2 }),
      }),
    );
    setFilterText("");
    setFilterText2("");
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAdd();
    }
  };

  const isRegex = condition === "matches_regex";
  const isNumeric = conditionMeta[condition].numericOnly;
  const hasRegexError =
    isRegex && filterText !== "" && !isValidRegex(filterText);
  const isLogTimeInput =
    selectedColumnId === LOG_TIME_ID && isDatetimeColumn(selectedDataType);
  const hasLogTimeError =
    isLogTimeInput &&
    filterText.trim() !== "" &&
    resolveLogTimeValue(filterText.trim(), timeDisplayMode, earliestTime) ===
      null;
  // Elapsed / ElapsedTime modes require earliestTime to anchor the filter
  // value. Block commit when the workspace has no time range rather than
  // silently resolving against an epoch-zero anchor.
  const timeRangeUnavailable =
    isLogTimeInput && !canCommitLogtime(timeDisplayMode, earliestTime);
  const hideTextField = isValuelessCondition(condition);
  const showSecondValue = needsSecondValue(condition);
  const useDatetimePicker = shouldUseDatetimePicker(
    selectedDataType,
    timeDisplayMode,
    condition,
  );

  const hasActiveFilters = Object.keys(columnFilters.filters).length > 0;

  // Mutual exclusivity: when __all__ has an active filter, hide per-column
  // options (except LOG_TIME_ID which is always available); when per-column
  // filters exist, hide __all__.
  const hasAll = hasAllColumnsFilter(columnFilters);
  const hasPerColumn = hasPerColumnFilters(columnFilters);
  const selectableColumns = React.useMemo(
    () =>
      Object.values(columnFilters.columns).filter((col) => {
        if (
          hasAll &&
          col.columnId !== ALL_COLUMNS_ID &&
          col.columnId !== LOG_TIME_ID
        ) {
          return false;
        }
        if (hasPerColumn && col.columnId === ALL_COLUMNS_ID) {
          return false;
        }
        return true;
      }),
    [columnFilters.columns, hasAll, hasPerColumn],
  );

  // Auto-switch to first available column when mutual exclusivity hides the
  // current selection (e.g. user adds a per-column filter while "All columns"
  // is selected).
  React.useEffect(
    function syncSelectedColumnWithSelectableOptions() {
      const isSelectable = selectableColumns.some(
        (col) => col.columnId === selectedColumnId,
      );
      if (!isSelectable && selectableColumns.length > 0) {
        handleColumnChange(selectableColumns[0].columnId);
      }
    },
    [selectableColumns, selectedColumnId, handleColumnChange],
  );

  if (hidden) {
    return null;
  }

  const showIndexAlert = searchIndexState !== IndexState.Built;

  return (
    <div className={styles.filterBar}>
      {showIndexAlert ? (
        <SearchIndexAlert
          searchIndexState={searchIndexState}
          needsBuildConfirmation={needsBuildConfirmation}
          topicMessageCount={topicMessageCount}
          onBuildIndex={onBuildIndex}
          onDismiss={onDismissBuildPrompt}
        />
      ) : (
        <div className={styles.builderRow}>
          <Select
            size="small"
            value={selectedColumnId}
            onChange={(event) => handleColumnChange(event.target.value)}
            disabled={disabled}
            className={styles.columnSelect}
            MenuProps={SELECT_MENU_PROPS}
            aria-label="Filter column"
          >
            {selectableColumns.map((col) => (
              <MenuItem key={col.columnId} value={col.columnId}>
                {col.name}
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={condition}
            onChange={(event) =>
              setCondition(event.target.value as FilterCondition)
            }
            disabled={disabled}
            className={styles.conditionSelect}
            MenuProps={SELECT_MENU_PROPS}
            aria-label="Filter condition"
          >
            {availableConditions.map((key) => (
              <MenuItem key={key} value={key}>
                {conditionMeta[key].label}
              </MenuItem>
            ))}
          </Select>
          {useDatetimePicker &&
            (showSecondValue ? (
              <DatetimeFilterInput
                mode="between"
                layout="split"
                from={filterText}
                to={filterText2}
                onChange={(from, to) => {
                  setFilterText(from);
                  setFilterText2(to);
                }}
                timeDisplayMode={timeDisplayMode}
                earliestTime={earliestTime}
                textFieldProps={{ className: styles.filterInput, disabled }}
              />
            ) : (
              <DatetimeFilterInput
                mode="single"
                value={filterText}
                onChange={(v) => setFilterText(v)}
                timeDisplayMode={timeDisplayMode}
                earliestTime={earliestTime}
                textFieldProps={{ className: styles.filterInput, disabled }}
              />
            ))}
          {!useDatetimePicker && !hideTextField && (
            <TextField
              size="small"
              className={styles.filterInput}
              type={
                isNumeric && !isDatetimeColumn(selectedDataType)
                  ? "number"
                  : "text"
              }
              placeholder={getFilterPlaceholder(isRegex, showSecondValue)}
              value={filterText}
              onChange={(event) => setFilterText(event.target.value)}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              error={hasRegexError || hasLogTimeError}
              aria-label="Filter value"
              inputRef={filterInputRef}
            />
          )}
          {!useDatetimePicker && showSecondValue && (
            <TextField
              size="small"
              className={styles.filterInput}
              type={isDatetimeColumn(selectedDataType) ? "text" : "number"}
              placeholder="Max"
              value={filterText2}
              onChange={(event) => setFilterText2(event.target.value)}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              aria-label="Filter maximum value"
            />
          )}
          <Button
            variant="contained"
            size="small"
            onClick={handleAdd}
            disabled={
              disabled ||
              (!isValuelessCondition(condition) && filterText.trim() === "") ||
              (showSecondValue && filterText2.trim() === "") ||
              hasRegexError ||
              timeRangeUnavailable
            }
            title={timeRangeUnavailable ? "Time range unavailable" : undefined}
            startIcon={<AddIcon />}
            aria-label="Add filter"
          >
            Add
          </Button>
          {/* Lives in the always-mounted builder row so appearing never
              reflows the pills row or the table below. */}
          {isApplyingFilter && (
            <Spinner size="sm" aria-label="Applying filters" />
          )}
        </div>
      )}
      {hasActiveFilters && (
        <ActiveFilterPills
          columnFilters={columnFilters}
          columnFiltersDispatch={columnFiltersDispatch}
          colorMap={colorMap}
          stageColors={stageColors}
          filterDisplayMode={filterDisplayMode}
          onFilterDisplayModeChange={onFilterDisplayModeChange}
          timeDisplayMode={timeDisplayMode}
          earliestTime={earliestTime}
        />
      )}
    </div>
  );
}

function ActiveFilterPills({
  columnFilters,
  columnFiltersDispatch,
  colorMap,
  stageColors,
  filterDisplayMode,
  onFilterDisplayModeChange,
  timeDisplayMode,
  earliestTime,
}: {
  columnFilters: ColumnFiltersState;
  columnFiltersDispatch: React.Dispatch<ColumnFiltersAction>;
  colorMap: Map<string, number>;
  stageColors: FilterStageColorVars[];
  filterDisplayMode: FilterDisplayMode;
  onFilterDisplayModeChange: (mode: FilterDisplayMode) => void;
  timeDisplayMode: TimeDisplayMode;
  earliestTime: bigint | null;
}) {
  // Sort by column ID for stable pill ordering across re-renders
  const activeFilterKeys = Object.keys(columnFilters.filters).sort();

  return (
    <div className={styles.pillsRow}>
      {activeFilterKeys.map((columnId) => (
        <FilterPill
          key={columnId}
          columnId={columnId}
          columnFilters={columnFilters}
          columnFiltersDispatch={columnFiltersDispatch}
          colorMap={colorMap}
          stageColors={stageColors}
          timeDisplayMode={timeDisplayMode}
          earliestTime={earliestTime}
        />
      ))}
      <FilterDisplayModeToggle
        mode={filterDisplayMode}
        onToggle={() =>
          onFilterDisplayModeChange(
            filterDisplayMode === "hide" ? "dim" : "hide",
          )
        }
      />
      <button
        className={styles.clearAll}
        onClick={() => columnFiltersDispatch(new ClearFiltersAction())}
        aria-label="Clear all filters"
      >
        Clear all
      </button>
    </div>
  );
}

function FilterPill({
  columnId,
  columnFilters,
  columnFiltersDispatch,
  colorMap,
  stageColors,
  timeDisplayMode,
  earliestTime,
}: {
  columnId: string;
  columnFilters: ColumnFiltersState;
  columnFiltersDispatch: React.Dispatch<ColumnFiltersAction>;
  colorMap: Map<string, number>;
  stageColors: FilterStageColorVars[];
  timeDisplayMode: TimeDisplayMode;
  earliestTime: bigint | null;
}) {
  const popup = usePopupState();
  const column = columnFilters.columns[columnId];
  const filter = columnFilters.filters[columnId];

  const { onStageChange, onStageAdd, onStageDelete } = useColumnFilterActions(
    columnId,
    columnFiltersDispatch,
  );

  const currentCombinator = filter.combinator;

  const onCombinatorChange = (combinator: "or" | "and") => {
    columnFiltersDispatch(new SetCombinatorAction({ columnId, combinator }));
  };

  const labelOptions: StageLabelOptions = {
    dataType: column?.dataType,
    timeDisplayMode,
    earliestTime,
  };

  const stripRef = React.useRef<HTMLDivElement>(null);

  return (
    <>
      <div className={styles.powerlineStrip} ref={stripRef}>
        <span className={styles.columnLabel}>
          {column?.name ?? columnId}:
          <button
            className={styles.deleteColumnButton}
            onClick={() =>
              columnFiltersDispatch(new DeleteFilterAction({ columnId }))
            }
            title="Remove filter"
            aria-label={`Remove filter for ${column?.name ?? columnId}`}
          >
            ×
          </button>
        </span>
        {filter.stages.map((stage, index) => {
          const colorIndex = colorMap.get(`${columnId}:${index}`);
          return (
            <React.Fragment key={`${stage.condition}-${stage.value}-${index}`}>
              {index > 0 && (
                <button
                  className={styles.combinatorSegment}
                  onClick={() =>
                    onCombinatorChange(
                      currentCombinator === "or" ? "and" : "or",
                    )
                  }
                  title={`Switch to ${currentCombinator === "or" ? "AND" : "OR"}`}
                  aria-label={`Toggle filter combinator, currently ${currentCombinator === "or" ? "OR" : "AND"}`}
                >
                  {currentCombinator === "or" ? "OR" : "AND"}
                </button>
              )}
              <button
                className={styles.stageSegment}
                style={
                  colorIndex !== undefined
                    ? stageColorStyle(colorIndex, stageColors)
                    : undefined
                }
                onClick={() => popup.open({ anchorEl: stripRef.current })}
                title={stageLabel(stage, labelOptions)}
                aria-label={`Filter: ${column?.name ?? columnId} ${stageLabel(stage, labelOptions)}`}
              >
                {stageLabel(stage, labelOptions)}
              </button>
            </React.Fragment>
          );
        })}
      </div>
      <ColumnFilterPopover
        popup={popup}
        columnName={column?.name ?? columnId}
        columnId={columnId}
        colorMap={colorMap}
        stageColors={stageColors}
        stages={filter.stages}
        dataType={column?.dataType}
        timeDisplayMode={timeDisplayMode}
        combinator={currentCombinator}
        onStageChange={onStageChange}
        onStageAdd={onStageAdd}
        onStageDelete={onStageDelete}
        onCombinatorChange={onCombinatorChange}
      />
    </>
  );
}

/**
 * Rendered in place of the filter builder row when the search index is empty
 * or building. Pills continue to render below this alert so that filters
 * loaded from saved layouts remain visible while the index is being built.
 */
function SearchIndexAlert({
  searchIndexState,
  needsBuildConfirmation,
  topicMessageCount,
  onBuildIndex,
  onDismiss,
}: {
  searchIndexState: IndexState;
  needsBuildConfirmation: boolean;
  topicMessageCount: number;
  onBuildIndex: () => void;
  onDismiss: () => void;
}) {
  // Empty + large log count: prompt before building. LogPanel does not
  // auto-build in this case, so the alert persists until the user clicks
  // Build Index or Dismiss.
  if (searchIndexState === IndexState.Empty && needsBuildConfirmation) {
    return (
      <Alert
        severity="warning"
        className={styles.indexAlert}
        action={
          <span className={styles.indexAlertActions}>
            <RobotoButton
              variant="text"
              size="small"
              color="inherit"
              onClick={onBuildIndex}
              eventName="LogPanelSearchIndexBuildConfirmed"
              eventProperties={{ logCount: topicMessageCount }}
            >
              Build Index
            </RobotoButton>
            <RobotoButton
              variant="text"
              size="small"
              color="inherit"
              onClick={onDismiss}
              eventName="LogPanelSearchIndexBuildCanceled"
              eventProperties={{ logCount: topicMessageCount }}
            >
              Dismiss
            </RobotoButton>
          </span>
        }
      >
        Filtering requires building a search index for{" "}
        {topicMessageCount.toLocaleString()} logs. This may take a while and may
        be constrained by available memory to your browser.
      </Alert>
    );
  }

  // Otherwise: actively building, or empty + small count (transient — LogPanel
  // auto-builds on open). Gate the full alert behind a short delay so that
  // sub-second builds on small files don't flash it.
  return <TransientBuildingIndicator />;
}

// How long to wait before escalating from a bare spinner to the full
// "Building search index…" alert. Small files build in well under this,
// so the alert never mounts and the transition to the builder row is clean.
const BUILDING_ALERT_DELAY_MS = 1000;

function TransientBuildingIndicator() {
  const [showAlert, setShowAlert] = React.useState(false);

  React.useEffect(() => {
    const timer = window.setTimeout(
      () => setShowAlert(true),
      BUILDING_ALERT_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, []);

  if (!showAlert) {
    return (
      <div
        className={styles.buildingSpinner}
        role="status"
        aria-label="Building search index"
      >
        <CircularProgress size="1rem" />
      </div>
    );
  }

  return (
    <Alert
      severity="info"
      icon={<CircularProgress size="1rem" />}
      className={styles.indexAlert}
    >
      Building search index…
    </Alert>
  );
}
